import type { DropdownOption } from '@/features/common/types/DropdownOption';
import type { FilterTag } from '@/features/common/types/FilterTag';

/** A period end as the listing endpoints take it — an ISO day. Anything else would be a 400. */
export const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** A filter naming a record by its id — a raion, a theme, an organiser. The endpoint answers 400 to
 * anything that is not a UUID, so a mangled link would blank the list instead of being ignored. */
export const RECORD_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The filters a public listing's URL holds, cleaned: trimmed, blanks left out, and a value kept only
 * when `acceptable` says the endpoint can take it. Read in the order `names` gives, so the request
 * built from the result is one string for one set of filters, however the URL was written.
 */
export function cleanFilters<N extends string>(
  params: URLSearchParams,
  names: readonly N[],
  acceptable: (name: N, value: string) => boolean,
): Partial<Record<N, string>> {
  const filters: Partial<Record<N, string>> = {};
  names.forEach((name) => {
    const value = params.get(name)?.trim();
    if (value && acceptable(name, value)) filters[name] = value;
  });
  return filters;
}

/** A period given backwards, turned the right way round — as the server reads it. ISO days compare
 * correctly as strings. */
export function orderPeriod<F extends { from?: string; to?: string }>(filters: F): F {
  if (filters.from && filters.to && filters.from > filters.to) {
    return { ...filters, from: filters.to, to: filters.from };
  }
  return filters;
}

/**
 * The URL after a change to some of a listing's filters: the changed ones set (or dropped when
 * emptied), the rest kept, and anything `clean` does not know — `page` among them — gone, since a
 * filter change starts the list again.
 *
 * Several at once, not one at a time: a period changes both its ends together, and two calls in a
 * row would each start from the same old URL, the second throwing the first away.
 */
export function withFilters<N extends string>(
  params: URLSearchParams,
  changes: Partial<Record<N, string | null>>,
  clean: (params: URLSearchParams) => Partial<Record<N, string>>,
): URLSearchParams {
  const next = searchParamsOf(clean(params));
  Object.entries<string | null | undefined>(changes).forEach(([name, value]) => {
    if (value && value.trim()) next.set(name, value.trim());
    else next.delete(name);
  });
  return searchParamsOf(clean(next));
}

/**
 * The tags under a listing's filter card: one per list filter in `names` that is set, named as its
 * list names the choice — the raion, the theme, the organiser, never the id the URL holds — in the
 * order of `names`. A value no list offers (the raions arrive after the calendar, an organiser can
 * leave it) gets no tag rather than a bare id; "Resetează" still clears it.
 */
export function activeFilterTags<N extends string>(
  names: readonly N[],
  filters: Partial<Record<N, string>>,
  choices: Record<N, readonly DropdownOption[]>,
): FilterTag<N>[] {
  return names.flatMap((name) => {
    const value = filters[name];
    const option = value ? choices[name].find((o) => o.value === value) : undefined;
    return option ? [{ name, label: option.label }] : [];
  });
}

/** The set filters as query parameters, in the order the object holds them; blanks left out. */
export function searchParamsOf(filters: Partial<Record<string, string>>): URLSearchParams {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([name, value]) => {
    if (value) params.set(name, value);
  });
  return params;
}
